'use client';
import type { Dispatch, Reducer } from 'react';
import React, { createContext, useReducer } from 'react';

import type { FabricActionTypes, FabricStateProps } from './reducers/fabric';
import FabricReducer, { initialState as FabricInitialState } from './reducers/fabric';
import type { GlobalActionTypes, GlobalStateProps } from './reducers/global';
import GlobalReducer, { initialState as GlobalInitialState } from './reducers/global';
import type { MissingSelectionValuesProps, MissingSelectionValuesTypes } from './reducers/missingSelectionValues';
import MissingSelectionValuesReducer, {
  initialState as MissingValuesSelectionInitialState,
} from './reducers/missingSelectionValues';
import type { NavigationActionTypes, NavigationStateProps } from './reducers/navigation';
import NavigationReducer, { initialState as NavigationInitialState } from './reducers/navigation';
import type { OptionActionTypes, OptionStateProps } from './reducers/option';
import OptionReducer, { initialState as OptionInitialState } from './reducers/option';
import {
  DeliveryPeriod,
  Fabric,
  FabricGroup,
  HiddenOptionsAndValues,
  Monogram,
  OptionsSelection,
  SizePassportSelection,
  StylePreset,
} from '../utils/interfaces';
import { ClientType } from '../utils/constants';
import { Locale } from '../i18n-config';
import { ProductType } from '../utils/productDefinition';
import { isFalsy } from '../utils/utils';
import {
  MissingMonogramValuesPerConfig,
  MissingOptionsPerConfiguration,
  MissingOptionValuesPerConfiguration,
} from '../utils/preloadConfiguration/preloadConfiguration';

export type State = FabricStateProps &
  NavigationStateProps &
  OptionStateProps &
  GlobalStateProps &
  MissingSelectionValuesProps;

export interface ContextProps {
  state: State;
  dispatch: Dispatch<
    FabricActionTypes | NavigationActionTypes | OptionActionTypes | GlobalActionTypes | MissingSelectionValuesTypes
  >;
}

interface ThemeProviderProps {
  selectedFabric: Fabric | null;
  selectedFabricGroup: FabricGroup | null;
  section: string | null;
  selectedOptions: OptionsSelection;
  hiddenOptionsAndValues: HiddenOptionsAndValues;
  selectedMonogram: Monogram;
  selectedBaseStyle: Record<string, StylePreset>;
  missingOptionValuesPerConfig: MissingOptionValuesPerConfiguration;
  missingMonogramValuesPerConfig: MissingMonogramValuesPerConfig;
  missingOptionsPerConfig: MissingOptionsPerConfiguration;
  monogramsIdsWithMissingSelection: number[];
  showMissingFabricModal: boolean;
  showMissingOptionsPanel: boolean;
  isAdditionalProductAdded: Record<string, boolean>;
  sizePassportSelection: SizePassportSelection;
  countryCode: string;
  client: ClientType;
  language: Locale;
  product: ProductType | undefined;
  deliveryPeriod: DeliveryPeriod | undefined;
  accountNr?: string;
  token?: string;
  storeID?: string;
  sizeId?: string;
  spCorrelationId?: string;
  children: React.ReactNode;
  growthbookFeatures: Record<string, any>;
}

export const ThemeContext = createContext<ContextProps>({} as ContextProps);
type CombinedReducer<S, A> = (state: S, action: A) => S;
const combineReducers =
  <S, A>(...reducers: Array<Reducer<S, A>>): CombinedReducer<S, A> =>
  (state: S, action: A) => {
    for (let i = 0; i < reducers.length; i++) {
      state = reducers[i](state, action);
    }
    return state;
  };

export default function ThemeProvider({
  selectedFabric,
  selectedFabricGroup,
  section,
  selectedOptions,
  hiddenOptionsAndValues,
  selectedMonogram,
  selectedBaseStyle,
  missingOptionValuesPerConfig,
  missingMonogramValuesPerConfig,
  missingOptionsPerConfig,
  monogramsIdsWithMissingSelection,
  showMissingFabricModal,
  showMissingOptionsPanel,
  isAdditionalProductAdded,
  sizePassportSelection,
  countryCode,
  language,
  client,
  product,
  deliveryPeriod,
  accountNr,
  token,
  storeID,
  sizeId,
  children,
  growthbookFeatures,
}: Readonly<ThemeProviderProps>) {
  const [state, dispatch] = useReducer(
    combineReducers<any, any>(
      FabricReducer,
      NavigationReducer,
      OptionReducer,
      GlobalReducer,
      MissingSelectionValuesReducer,
    ),
    {
      ...FabricInitialState,
      ...NavigationInitialState,
      ...OptionInitialState,
      ...GlobalInitialState,
      ...MissingValuesSelectionInitialState,
      selectedFabric,
      selectedFabricGroup,
      section,
      selectedOptions,
      hiddenOptionsAndValues,
      selectedMonogram,
      selectedBaseStyle,
      missingOptionValuesPerConfig,
      missingMonogramValuesPerConfig,
      missingOptionsPerConfig,
      monogramsIdsWithMissingSelection,
      showMissingFabricModal,
      showMissingOptionsPanel,
      isAdditionalProductAdded,
      sizePassportSelection,
      countryCode,
      language,
      client,
      product,
      deliveryPeriod,
      accountNr,
      token,
      storeID,
      sizeId,
      isUserPreselected: !isFalsy(accountNr),
      growthbookFeatures,
    },
  );
  const value = { state, dispatch };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
